import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import { DownloadJpg } from '../../../../common/downloadJpg'
import { UseI18n } from '../../../../common/hooks/useI18n'
import { Element } from '../../../../common/html/element'
import { Panel } from '../../../../common/panel'
import { SCREEN } from '../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../common/selectCurrencyUnit/useFormatCurrency'
import { SelectCustom } from '../../../../common/selectCustom'
import { selectCompanyInfo } from '../../../common/header/companyInfo/store/slice'
import { selectCompareIndustry, selectDefaultIndustry } from '../store/slice'
import CompareSelect from './CompareSelect'
import NumberOfCompanies from './component/numberOfCompanies'
import RatioComparison from './component/ratioComparison'
import Size from './component/size'
import { COMPARE_TYPE, COMPARE_TYPE_DATA, mapViewBy } from './constant'
import { selectCompareVal, selectData } from './store/slice'
import {
  getFdiDomestic,
  getLargeSme,
  getListedLarge,
  sectorDefaultVsicIdPerformanceAnalyzerCompareExport,
  sectorDefaultVsicIdPerformanceAnalyzerExport,
} from './store/thunk'
import ViewByTable from './ViewByTable'

const SectorPerformance = () => {
  const dispatch = useDispatch()
  const label = UseI18n(
    'companyAnalysis.industryAnalysis.SECTOR_PERFORMANCE_ANALYSIS',
  )
  const location = useLocation()
  const locale = useSelector((state) => state.i18n.locale)
  const vsicDefault = useSelector(selectDefaultIndustry)
  const vsicCompare = useSelector(selectCompareIndustry)
  const compareVal = useSelector(selectCompareVal)
  const { organizationId } = new Proxy(new URLSearchParams(location.search), {
    get: (searchParam, prop) => searchParam.get(prop),
  })
  const { organizationShortName, organizationName, taxCode } =
    useSelector(selectCompanyInfo)
  const { currency, multiplier, unitName } = useFormatCurrency(
    SCREEN.INDUSTRY_ANALYSIS,
  )
  const [viewBy, setViewBy] = useState(mapViewBy.chart.value)
  const isViewByChart = viewBy === mapViewBy.chart.value
  const data = useSelector(selectData)
  const compareBetweenText = UseI18n(
    COMPARE_TYPE_DATA.find((item) => item.value === compareVal)?.label,
  )
  const handleChooseViewBy = (item) => {
    if (item.value !== viewBy) {
      setViewBy(item.value)
    }
  }

  useEffect(() => {
    switch (compareVal) {
      case COMPARE_TYPE.LARGE_SME:
        dispatch(
          getLargeSme({
            DefaultVSICId: vsicDefault,
            CompareVSICId: vsicCompare,
            OrganizationId: organizationId,
          }),
        )
        break
      case COMPARE_TYPE.LISTED_LARGE:
        dispatch(
          getListedLarge({
            DefaultVSICId: vsicDefault,
            CompareVSICId: vsicCompare,
            OrganizationId: organizationId,
          }),
        )
        break
      default:
        dispatch(
          getFdiDomestic({
            DefaultVSICId: vsicDefault,
            CompareVSICId: vsicCompare,
            OrganizationId: organizationId,
          }),
        )
        break
    }
  }, [vsicDefault, vsicCompare, locale, compareVal])

  const downloadCsv = () => {
    if (vsicCompare) {
      return sectorDefaultVsicIdPerformanceAnalyzerCompareExport({
        OrganizationId: organizationId,
        defaultVSICId: vsicDefault,
        compareVsicId: vsicCompare,
        compareIndustry: compareVal,
        currency,
        currencyUnit: { multiplier, value: unitName, en_Value: unitName },
        compareIndustryText: compareBetweenText,
      })
    } else {
      return sectorDefaultVsicIdPerformanceAnalyzerExport({
        OrganizationId: organizationId,
        defaultVSICId: vsicDefault,
        compareIndustry: compareVal,
        currency,
        currencyUnit: { multiplier, value: unitName, en_Value: unitName },
        compareIndustryText: compareBetweenText,
      })
    }
  }

  const renderRightHeader = () => {
    const defaultData = data[compareVal]?.defaultIndustry
    const compareData = data[compareVal]?.comparingIndustry
    const labelSize = UseI18n('companyAnalysis.industryAnalysis.SIZE', {
      year: defaultData?.sizeYear || compareData?.sizeYear || '',
    })
    const labelNumberOfCompanies = UseI18n(
      'companyAnalysis.industryAnalysis.NUMBER_OF_COMPANIES',
      { year: defaultData?.nocYear || compareData?.nocYear || '' },
    )
    const labelRatioComparison = UseI18n(
      'companyAnalysis.industryAnalysis.RATIO_COMPARISON',
      { year: defaultData?.ratioYear || compareData?.ratioYear || '' },
    )
    return (
      <Element
        className="dFlex alignItemsCenter"
        style={{ color: '#3A414B', marginRight: isViewByChart ? 0 : -16 }}
      >
        <Translate value="financialAnalysis.VIEW_BY" />
        <SelectCustom
          isBorderNone
          isI18n
          activeItem={Object.values(mapViewBy).find(
            (item) => item.value === viewBy,
          )}
          data={Object.values(mapViewBy)}
          chooseItem={handleChooseViewBy}
        />
        {isViewByChart && (
          <DownloadJpg
            data={{
              arrName: [
                label,
                labelSize,
                organizationShortName || organizationName || taxCode || '',
              ],
              titleJpg: labelSize,
              domId: 'sizeChartId',
            }}
          >
            <DownloadJpg
              data={{
                arrName: [
                  label,
                  labelNumberOfCompanies,
                  organizationShortName || organizationName || taxCode || '',
                ],
                titleJpg: labelNumberOfCompanies,
                domId: 'numberOfCompaniesCharId',
              }}
            >
              <DownloadJpg
                data={{
                  arrName: [
                    label,
                    labelRatioComparison,
                    organizationShortName || organizationName || taxCode || '',
                  ],
                  titleJpg: labelRatioComparison,
                  domId: 'ratioComparisonChartId',
                }}
              />
            </DownloadJpg>
          </DownloadJpg>
        )}
      </Element>
    )
  }
  return (
    <Panel
      label={label}
      renderRightHeader={renderRightHeader}
      downloadCsv={
        isViewByChart
          ? undefined
          : {
              disabled: false,
              downloadFunc: downloadCsv,
            }
      }
    >
      <div
        style={{
          padding: '8px 12px',
        }}
      >
        <CompareSelect />
        {viewBy === mapViewBy.chart.value ? (
          <>
            <div className="dFlex gap4">
              <div className="w50">
                <Size viewBy={viewBy} />
              </div>
              <div className="w50">
                <NumberOfCompanies viewBy={viewBy} />
              </div>
            </div>
            <RatioComparison viewBy={viewBy} />
          </>
        ) : (
          <ViewByTable />
        )}
      </div>
    </Panel>
  )
}

export default SectorPerformance
